/**
 * Tentative — diagnoses held back because the reading behind them is in doubt,
 * for a reviewer to settle.
 *
 * Each row is an attempt whose diagnosis is recorded but not used: the readers
 * disagreed on the answer, a line was read doubtfully, or the work may belong to
 * another question. Opening a row goes to the analysis detail page, whose
 * Evidence panel takes one answer per doubtful unit. Wrong answers come first,
 * then diagnoses that would make one of the student's patterns recur.
 *
 * See docs/FeatureDesigns/diagnosis/accuracy/diagnosis-selection-target.md §11.
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DataTable, type ColumnDef } from '@shared-ui/components';
import { fetchWithAdmin } from '../../api/fetchHelper';
import { formatWhen } from './format';
import { REASON_TEXT, UNIT_TEXT } from '../operations/evidenceText';

export interface TentativeRow {
  submissionId: string;
  userId: string;
  userName?: string;
  questionId?: string;
  questionText?: string;
  submittedAt?: string;
  cameOutRight?: boolean;
  cause?: string;
  reasons: string[];
  unitsToSettle: string[];
  couldRecur: boolean;
}

export function TentativeDiagnosesPanel() {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['tentativeDiagnoses'],
    queryFn: () => fetchWithAdmin<{ rows: TentativeRow[]; scanned: number }>('/console/diagnoses/tentative?limit=100'),
  });

  const columns: ColumnDef<TentativeRow>[] = [
    { key: 'submittedAt', header: 'when', mobile: 'title', className: 'lp-col-when', render: r => (r.submittedAt ? formatWhen(r.submittedAt) : '—') },
    { key: 'userId', header: 'student', className: 'lp-col-student', render: r => <span className="lp-name">{r.userName ?? r.userId}</span> },
    { key: 'question', header: 'question', render: r => <span className="lp-cause-cell">{r.questionText ?? r.questionId ?? '—'}</span> },
    { key: 'answer', header: 'answer', className: 'lp-col-trend', render: r => (r.cameOutRight === undefined ? '—' : r.cameOutRight ? 'right' : 'wrong') },
    { key: 'cause', header: 'diagnosis', render: r => <span className="lp-cause-cell">{r.cause ?? '—'}</span> },
    { key: 'reasons', header: 'why tentative', render: r => r.reasons.map(x => REASON_TEXT[x] ?? x).join('; ') },
    { key: 'units', header: 'to settle', className: 'lp-dim', render: r => r.unitsToSettle.map(u => UNIT_TEXT(u)).join(', ') },
    {
      key: 'couldRecur', header: '', className: 'lp-col-when',
      render: r => (r.couldRecur ? <span className="lp-chip is-habit" title="The student already has this kind of diagnosis counting">would recur</span> : null),
    },
  ];

  return (
    <>
      {q.isLoading && <p className="lp-muted">Loading…</p>}
      {q.isError && <p className="lp-error">Failed: {(q.error as Error).message}</p>}
      {q.data && (
        <p className="lp-count">
          {q.data.rows.length} to settle, from {q.data.scanned} tentative diagnoses in the most recent scan
        </p>
      )}
      {q.data && (
        <DataTable
          data={q.data.rows}
          columns={columns}
          rowKey={r => r.submissionId}
          onRowClick={r => navigate(`/submissions/${r.submissionId}`)}
          emptyMessage="No diagnosis is waiting on a reading."
        />
      )}
    </>
  );
}
